<template>
  <div
    v-if="isAdmin && !$_.get(basketData, 'isGuestBasket')"
    class="is-flex has-items-aligned-center"
  >
    <div :style="{ lineHeight: 1.25 }" class="has-text-right">
      <p class="has-margin-bottom-25">
        <u-link :to="clientBasketsLink">
          <strong>{{ clientName }}</strong>
        </u-link>
      </p>
      <!-- Price list (if not default) -->
      <template v-if="hasNonDefaultPricelist">
        <i18n tag="u-info" path="_.pricelist_object">
          <template #pricelist>
            <u-link :to="clientBillingSettingsRoute">{{
              account.pricelist.name
            }}</u-link>
          </template>
        </i18n>
      </template>
      <!-- Email -->
      <p v-else-if="$_.has(client, 'default_email.email')">
        {{ client.default_email.email }}
      </p>
    </div>
    <b-dropdown
      :mobile-modal="false"
      aria-role="menu"
      position="is-bottom-left"
    >
      <template #trigger>
        <avatar
          :size="48"
          :name="$_.get(client, 'fullname')"
          :src="$_.get(client, 'image_url')"
          class="has-margin-left-75 has-cursor-pointer"
        >
          <template #bottom-right>
            <AvatarChevronDown />
          </template>
        </avatar>
      </template>
      <b-dropdown-item aria-role="menuitem" has-link>
        <router-link :to="clientBasketsLink">
          {{
            $t("_action.view_object", {
              object: $t("_.profile").toString().toLowerCase()
            })
          }}
        </router-link>
      </b-dropdown-item>
      <hr
        v-if="!basket || !$_.get(basketData, 'basketIsComplete')"
        class="dropdown-divider"
      />
      <b-dropdown-item v-if="!basket" aria-role="menuitem" has-link>
        <a @click="$basketMethods.openSelectClientModal()">
          <i18n path="_action.change_client" />
        </a>
      </b-dropdown-item>
      <b-dropdown-item
        v-if="!$_.get(basketData, 'basketIsComplete')"
        aria-role="menuitem"
        has-link
      >
        <a class="has-text-danger" @click="$basketMethods.cancelOrder()">
          <i18n path="_action.cancel_order" />
        </a>
      </b-dropdown-item>
    </b-dropdown>
  </div>
  <div v-else-if="basketData.isGuestBasket || isGuest">
    <b-dropdown
      :mobile-modal="false"
      aria-role="menu"
      position="is-bottom-left"
    >
      <template #trigger>
        <avatar
          :clickable="true"
          :has-dropdown="true"
          :size="48"
          :uploadable="false"
          class="has-margin-left-75 has-cursor-pointer"
        >
          <template #bottom-right>
            <AvatarChevronDown />
          </template>
        </avatar>
      </template>
      <b-dropdown-item :has-link="true" aria-role="menuitem">
        <a class="is-size-7" @click="$basketMethods.openLoginModal">
          <i18n path="_action.login" tag="u" />
        </a>
      </b-dropdown-item>
      <b-dropdown-item v-if="!isAdmin" :has-link="true" aria-role="menuitem">
        <u-link :to="{ name: clientRegisterRoute }" class="is-size-7">
          <i18n path="_action.register" />
        </u-link>
      </b-dropdown-item>
    </b-dropdown>
  </div>
  <div v-else-if="basketData.clientIsGuest">
    <b-dropdown
      :mobile-modal="false"
      aria-role="menu"
      position="is-bottom-left"
    >
      <template #trigger>
        <avatar
          :clickable="true"
          :has-dropdown="true"
          :size="48"
          :uploadable="false"
          class="has-margin-left-75 has-cursor-pointer"
        >
          <template #bottom-right>
            <AvatarChevronDown />
          </template>
        </avatar>
      </template>
      <b-dropdown-item :has-link="true" aria-role="menuitemt">
        <a
          class="is-size-7"
          @click="$basketMethods.openCompleteRegistrationLogin"
        >
          <i18n path="_action.register" tag="u" />
        </a>
      </b-dropdown-item>
      <hr aria-role="menuitem" class="dropdown-divider" />
      <b-dropdown-item :has-link="true" aria-role="menuitem">
        <router-link :to="logoutRoute" class="has-text-danger">
          <b-icon
            class="has-margin-right-50"
            icon="sign-out-alt"
            size="is-small"
          />
          <i18n path="_action.logout" tag="span" />
        </router-link>
      </b-dropdown-item>
    </b-dropdown>
  </div>
  <profile-dropdown v-else context="client" />
</template>

<script lang="ts">
import { defineComponent } from "vue";
import Context from "@/mixins/context";
import ClientMixin from "@/mixins/clientMixin";
import { AdminRoutes, ClientRoutes } from "@/data/enums/router";
import type {
  IBasketProviderData
  // IBasketProviderMethods
} from "@/models/providers/baskets";
import type { Location as VueLocation } from "vue-router";

export default defineComponent({
  name: "BasketAvatar",
  components: {
    "profile-dropdown": () =>
      import("@/components/app/global/user/profileDropdown.vue")
  },
  mixins: [Context, ClientMixin],
  inject: {
    $basketData: { from: "basketProviderData" },
    $basketMethods: { from: "basketProviderMethods" }
  },
  data: () => ({
    clientRegisterRoute: ClientRoutes.REGISTER
  }),
  computed: {
    basketData(): IBasketProviderData {
      return this.$basketData();
    },
    client() {
      return this.$_.get(this.basketData, "client");
    },
    account() {
      return this.$_.get(this.basketData, "account");
    },
    basket() {
      return this.$_.get(this.basketData, "basket");
    },
    logoutRoute(): VueLocation {
      return {
        name: this.isAdmin ? AdminRoutes.LOGOUT : ClientRoutes.LOGOUT,
        params: {
          toast: this.$t("_.see_you_soon") as string
        }
      };
    },
    clientBillingSettingsRoute() {
      return {
        name: AdminRoutes.CLIENT_SETTINGS_BILLING,
        params: {
          clientId: this.client.id
        }
      };
    },
    clientName() {
      return this.getClientName({
        client: this.client
      });
    },
    clientBasketsLink() {
      return {
        name: "adminClientBaskets",
        params: { clientId: this.client?.id || this.basketData.clientId }
      };
    },
    hasNonDefaultPricelist() {
      return (
        !this.$_.isEmpty(this.account) &&
        this.account.pricelist_id !== this.account.brand.pricelist_id
      );
    }
  }
});
</script>
